import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Building2, Pencil, RefreshCw } from "lucide-react";

const EMPTY = { name: "", code: "", description: "" };

export default function Departments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");

  const load = () =>
    api.get("/departments").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setErr("");
    setOpen(true);
  };

  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({ name: d.name || "", code: d.code || "", description: d.description || "" });
    setErr("");
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setEditingId(null);
    setForm(EMPTY);
    setErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      if (editingId) {
        await api.put(`/departments/${editingId}`, form);
      } else {
        await api.post("/departments", form);
      }
      close();
      load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed");
    }
  };

  return (
    <AdminLayout title="Departments" breadcrumbs={["Home", "Organization", "Departments"]}>
      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">All Departments</h3>
          <div className="flex items-center gap-2">
            <button onClick={load} title="Refresh" data-testid="dep-refresh-btn" className="p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              data-testid="add-department-btn"
              onClick={openCreate}
              className="inline-flex items-center gap-2 bg-[#089949] hover:bg-[#067A3A] text-white px-4 py-2 text-sm rounded font-medium"
            >
              <Plus className="h-4 w-4" /> Add Department
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
          {items.map((d) => (
            <div key={d.id} className="border border-slate-200 rounded p-4 hover:shadow-sm transition relative group">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded bg-[#226DB4]/10 text-[#226DB4] flex items-center justify-center">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800">{d.name}</div>
                    <div className="text-xs text-slate-500">Code: {d.code}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(d)}
                  data-testid={`edit-department-${d.id}`}
                  title="Edit department"
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-slate-200 rounded text-slate-600 hover:bg-slate-50 hover:text-[#226DB4]"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
              </div>
              <p className="text-sm text-slate-600">{d.description || "—"}</p>
            </div>
          ))}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form
            onSubmit={submit}
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4"
          >
            <h3 className="text-lg font-semibold text-slate-800">
              {editingId ? "Edit Department" : "New Department"}
            </h3>
            <input
              required
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
              data-testid="dep-name-input"
            />
            <input
              required
              placeholder="Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
              data-testid="dep-code-input"
            />
            <textarea
              placeholder="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
            />
            {err && <div className="text-sm text-rose-600">{String(err)}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={close} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded" data-testid="dep-save-btn">
                {editingId ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
